/**
 * LazyGlobe -- lazy-loaded Three.js globe with IntersectionObserver visibility
 * gating, DPR clamping, and prefers-reduced-motion / WebGL fallbacks.
 *
 * Dark ocean sphere (#0b2130) with 30 violet constellation points and a
 * subtle additive-blend atmosphere shell. Rotation speed doubles when
 * `pulse` is true (agent active state).
 */
import { useRef, useEffect, useState, type ReactNode } from 'react'

/* ---------- helpers ------------------------------------------------ */

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window)) return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)')
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  return reduced
}

function supportsWebGL(): boolean {
  if (typeof document === 'undefined') return false
  try {
    const c = document.createElement('canvas')
    return !!(c.getContext('webgl') || c.getContext('webgl2'))
  } catch {
    return false
  }
}

/* ---------- static fallback SVG ------------------------------------ */

function GlobeFallbackSVG({ width, height }: { width: string; height: string }) {
  return (
    <svg
      viewBox="0 0 120 120"
      style={{ width, height, display: 'block' }}
      aria-hidden="true"
    >
      <circle cx="60" cy="60" r="54" fill="#0b2130" />
      <circle cx="60" cy="60" r="56" fill="none" stroke="#a78bfa" strokeWidth="0.5" opacity="0.25" />
      {/* A few constellation dots */}
      {[30, 75, 120, 200, 260, 310].map((deg) => {
        const rad = (deg * Math.PI) / 180
        return (
          <circle
            key={deg}
            cx={60 + Math.cos(rad) * 40}
            cy={60 + Math.sin(rad) * 40}
            r="1.5"
            fill="#a78bfa"
            opacity="0.7"
          />
        )
      })}
    </svg>
  )
}

/* ---------- component ---------------------------------------------- */

interface LazyGlobeProps {
  width?: string
  height?: string
  pulse?: boolean
  fallback?: ReactNode
}

export default function LazyGlobe({
  width = '280px',
  height = '280px',
  pulse = false,
  fallback,
}: LazyGlobeProps) {
  const mountRef = useRef<HTMLDivElement>(null)
  const frameRef = useRef<number | undefined>(undefined)
  const reduced = usePrefersReducedMotion()
  const [isReady, setIsReady] = useState(false)
  const hasWebGL = supportsWebGL()

  const resolvedFallback = fallback ?? <GlobeFallbackSVG width={width} height={height} />

  useEffect(() => {
    if (!mountRef.current || !hasWebGL) return

    let isMounted = true
    let cleanupFn: (() => void) | undefined

    ;(async () => {
      const THREE = await import('three')
      if (!isMounted || !mountRef.current) return

      const container = mountRef.current
      const rect = container.getBoundingClientRect()
      const dpr = Math.min(2, window.devicePixelRatio || 1)

      /* ---- renderer ---- */
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
      renderer.setPixelRatio(dpr)
      renderer.setSize(rect.width, rect.height, false)
      renderer.domElement.style.width = '100%'
      renderer.domElement.style.height = '100%'
      renderer.domElement.style.display = 'block'
      container.appendChild(renderer.domElement)

      /* ---- scene + camera ---- */
      const scene = new THREE.Scene()
      const camera = new THREE.PerspectiveCamera(40, rect.width / rect.height, 0.1, 1000)
      camera.position.set(0, 0, 3.3)

      /* ---- lights ---- */
      scene.add(new THREE.AmbientLight(0xffffff, 0.6))
      const dir = new THREE.DirectionalLight(0xffffff, 0.6)
      dir.position.set(5, 3, 5)
      scene.add(dir)

      /* ---- globe (dark ocean) ---- */
      const R = 1.0
      const globeGeom = new THREE.SphereGeometry(R, 64, 64)
      const globeMat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(0x0b2130),
        metalness: 0.05,
        roughness: 0.7,
      })
      const globe = new THREE.Mesh(globeGeom, globeMat)
      scene.add(globe)

      /* ---- constellation points ---- */
      const pointCount = 30
      const positions = new Float32Array(pointCount * 3)
      for (let i = 0; i < pointCount; i++) {
        const theta = Math.random() * Math.PI * 2
        const phi = Math.random() * Math.PI - Math.PI / 2
        const r = R + 0.01 + Math.random() * 0.02
        positions[i * 3 + 0] = Math.cos(theta) * Math.cos(phi) * r
        positions[i * 3 + 1] = Math.sin(phi) * r
        positions[i * 3 + 2] = Math.sin(theta) * Math.cos(phi) * r
      }
      const pointsGeom = new THREE.BufferGeometry()
      pointsGeom.setAttribute('position', new THREE.BufferAttribute(positions, 3))
      const pointsMat = new THREE.PointsMaterial({
        color: 0xa78bfa,
        size: 0.02,
        transparent: true,
        opacity: 0.9,
      })
      const points = new THREE.Points(pointsGeom, pointsMat)
      scene.add(points)

      /* ---- constellation lines connecting nearby points ---- */
      const lineSegments: number[] = []
      const maxLineDist = 0.9 // only connect points within this distance
      const maxLines = 12
      let lineCount = 0
      for (let i = 0; i < pointCount && lineCount < maxLines; i++) {
        for (let j = i + 1; j < pointCount && lineCount < maxLines; j++) {
          const dx = positions[i * 3] - positions[j * 3]
          const dy = positions[i * 3 + 1] - positions[j * 3 + 1]
          const dz = positions[i * 3 + 2] - positions[j * 3 + 2]
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz)
          if (dist < maxLineDist) {
            lineSegments.push(
              positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2],
              positions[j * 3], positions[j * 3 + 1], positions[j * 3 + 2],
            )
            lineCount++
          }
        }
      }
      const linesGeom = new THREE.BufferGeometry()
      linesGeom.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(lineSegments, 3),
      )
      const linesMat = new THREE.LineBasicMaterial({
        color: 0xa78bfa,
        transparent: true,
        opacity: 0.15,
        depthWrite: false,
      })
      const lines = new THREE.LineSegments(linesGeom, linesMat)
      scene.add(lines)

      /* ---- atmosphere shell ---- */
      const atmoGeom = new THREE.SphereGeometry(R * 1.04, 64, 64)
      const atmoMat = new THREE.MeshBasicMaterial({
        color: 0xa78bfa,
        transparent: true,
        opacity: 0.04,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      })
      const atmosphere = new THREE.Mesh(atmoGeom, atmoMat)
      scene.add(atmosphere)

      /* ---- animation loop ---- */
      let last = performance.now()
      let paused = false
      const targetFPS = reduced ? 15 : 50
      const frameInterval = 1000 / targetFPS
      /* Smooth opacity pulse: lerp toward target opacity based on pulse state */
      let currentOpacity = 0.9
      let targetOpacity = 0.9

      function step(now: number) {
        frameRef.current = requestAnimationFrame(step)
        const elapsed = now - last
        if (elapsed < frameInterval) return
        last = now - (elapsed % frameInterval)

        if (!paused) {
          const dt = elapsed / 1000
          const speed = pulse ? 0.12 : 0.06
          globe.rotation.y += speed * dt
          atmosphere.rotation.y += speed * dt * 1.02
          points.rotation.y = globe.rotation.y
          lines.rotation.y = globe.rotation.y
          lines.rotation.x = globe.rotation.x
          globe.rotation.x = Math.sin(now * 0.0005) * 0.02

          /* Gentle opacity pulse on route change (pulse=true) */
          targetOpacity = pulse ? 1.0 : 0.9
          currentOpacity += (targetOpacity - currentOpacity) * Math.min(1, dt * 3)
          pointsMat.opacity = currentOpacity
          linesMat.opacity = 0.15 * (currentOpacity / 0.9)
          atmoMat.opacity = 0.04 + (pulse ? Math.sin(now * 0.004) * 0.015 : 0)
        }
        renderer.render(scene, camera)
      }

      /* ---- IntersectionObserver pause/resume ---- */
      const io = new IntersectionObserver(
        (entries) => {
          for (const ent of entries) {
            paused = !ent.isIntersecting
          }
        },
        { root: null, threshold: 0.1 },
      )
      io.observe(container)

      /* ---- resize handler ---- */
      const onResize = () => {
        if (!container) return
        const r = container.getBoundingClientRect()
        renderer.setSize(r.width, r.height, false)
        camera.aspect = r.width / r.height
        camera.updateProjectionMatrix()
      }
      window.addEventListener('resize', onResize)

      /* ---- start ---- */
      if (isMounted) setIsReady(true)
      frameRef.current = requestAnimationFrame(step)

      /* ---- cleanup closure ---- */
      cleanupFn = () => {
        if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current)
        io.disconnect()
        window.removeEventListener('resize', onResize)
        renderer.dispose()
        globeGeom.dispose()
        globeMat.dispose()
        pointsGeom.dispose()
        pointsMat.dispose()
        atmoGeom.dispose()
        atmoMat.dispose()
        linesGeom.dispose()
        linesMat.dispose()
        if (container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement)
        }
      }
    })()

    return () => {
      isMounted = false
      cleanupFn?.()
    }
  }, [pulse, reduced, hasWebGL])

  /* No WebGL or reduced motion -- render static fallback */
  if (!hasWebGL || reduced) {
    return (
      <div style={{ width, height }} aria-hidden="true">
        {resolvedFallback}
      </div>
    )
  }

  return (
    <div ref={mountRef} style={{ width, height, position: 'relative' }} aria-hidden="true">
      {!isReady && resolvedFallback}
    </div>
  )
}
